import Intercom, {
  boot,
  onHide,
  onShow,
  onUnreadCountChange,
  show,
  shutdown,
  update,
} from '@intercom/messenger-js-sdk';
import type { MessengerSession } from 'src/api/generated';
import type { LanguageCode } from 'src/modules/shared/types/i18n/i18n.type';
import type { SupportState } from '../types/support.types';
import { clearIntercomCookies } from './intercom-cookies.util';

const UPDATE_INTERVAL_MS = 120_000;
const BOOT_TIMEOUT_MS = 15_000;
// Messenger uses language codes rather than the app's regional locales.
const MESSENGER_LANGUAGES: Record<LanguageCode, string> = {
  'es-CL': 'es',
  'en-US': 'en',
};

/** Owns SDK lifecycle, stale callbacks and bounded SPA updates for one app user. */
export class SupportMessenger {
  private session: MessengerSession | null = null;
  private generation = 0;
  private bootTimer?: ReturnType<typeof setTimeout>;
  private expiryTimer?: ReturnType<typeof setTimeout>;
  private routeTimer?: ReturnType<typeof setTimeout>;
  private lastUpdate = 0;
  private state: SupportState = {
    status: 'loading',
    unreadCount: 0,
    isOpen: false,
  };

  /** @param onChange - Receives Messenger status and unread message changes. */
  constructor(private readonly onChange: (state: SupportState) => void) {}

  private publish(patch: Partial<SupportState>): void {
    this.state = { ...this.state, ...patch };
    this.onChange(this.state);
  }

  /**
   * Boots a secure identity or refreshes its token without closing a conversation.
   * @param session - Server-issued connection details.
   * @param language - Application language for the Messenger interface.
   */
  connect(session: MessengerSession, language: LanguageCode): void {
    if (session.expiresAt * 1000 <= Date.now()) {
      this.stop();
      return;
    }
    const settings = {
      app_id: session.appId,
      api_base: session.apiBase,
      intercom_user_jwt: session.token,
      hide_default_launcher: true,
      language_override: MESSENGER_LANGUAGES[language],
    };
    const sameIdentity =
      this.session?.appId === session.appId &&
      this.session.userId === session.userId;
    if (sameIdentity) {
      update(settings);
    } else {
      this.stop();
      this.session = session;
      this.publish({ status: 'loading' });
      const generation = this.generation;
      if (!window.Intercom) {
        // The npm initializer sets regional API defaults. Preserve the server's region.
        const region = session.apiBase.includes('.eu.')
          ? 'eu'
          : session.apiBase.includes('.au.')
            ? 'ap'
            : 'us';
        Intercom({ ...settings, region });
      } else {
        boot(settings);
      }
      const active = () => {
        const current = this.session;
        return (
          generation === this.generation &&
          current !== null &&
          current.expiresAt * 1000 > Date.now()
        );
      };
      this.bootTimer = setTimeout(() => {
        if (active()) this.publish({ status: 'unavailable' });
      }, BOOT_TIMEOUT_MS);
      window.Intercom?.('ready', () => {
        if (!active()) return;
        clearTimeout(this.bootTimer);
        this.publish({ status: 'ready' });
      });
      onUnreadCountChange((count: number) => {
        if (active()) {
          this.publish({
            unreadCount: Number.isFinite(count)
              ? Math.max(0, Math.floor(count))
              : 0,
          });
        }
      });
      onShow(() => {
        if (active()) this.publish({ isOpen: true });
      });
      onHide(() => {
        if (active()) this.publish({ isOpen: false });
      });
    }
    this.session = session;
    this.lastUpdate = Date.now();
    clearTimeout(this.expiryTimer);
    this.expiryTimer = setTimeout(
      () => this.stop(),
      session.expiresAt * 1000 - Date.now()
    );
  }

  /** Opens Home or unread conversations, with native access to Tickets. @returns Whether Messenger opened. */
  open(): boolean {
    if (!this.session || this.session.expiresAt * 1000 <= Date.now()) {
      this.stop();
      return false;
    }
    if (this.state.status !== 'ready') return false;
    show();
    return true;
  }

  /** Coalesces route impressions to at most 15 updates per 30 minutes. */
  routeChanged(): void {
    clearTimeout(this.routeTimer);
    this.routeTimer = setTimeout(
      () => {
        if (!this.session || this.state.status !== 'ready') return;
        if (this.session.expiresAt * 1000 <= Date.now()) {
          this.stop();
          return;
        }
        update({
          intercom_user_jwt: this.session.token,
          hide_default_launcher: true,
        });
        this.lastUpdate = Date.now();
      },
      Math.max(500, UPDATE_INTERVAL_MS - (Date.now() - this.lastUpdate))
    );
  }

  /** Invalidates callbacks, clears identity and cookies, and resets every launcher. */
  stop(): void {
    this.generation += 1;
    clearTimeout(this.bootTimer);
    clearTimeout(this.expiryTimer);
    clearTimeout(this.routeTimer);
    if (window.Intercom) shutdown();
    delete window.intercomSettings;
    clearIntercomCookies();
    this.session = null;
    this.publish({ status: 'unavailable', unreadCount: 0, isOpen: false });
  }
}
